import React, { useEffect, useState } from "react";
import { ScrollableContentLayout, CommonPagination } from "../../components/common";
import { Card } from "../../components/card";
import RewardHistoryTable from "./components/RewardHistoryTable";
import ResendRewardModal from "./components/ResendRewardModal";
import { RewardHistoryItem } from "./types/RewardHistory";
import { useRewardHistory } from "./hooks/useRewardHistory";
import { CheckCircle2, Send, RefreshCw } from "lucide-react";
import { Button } from "../../components/button";
import { useTranslation } from "react-i18next";
import { RewardHistoryStatistic } from "./api/reward-history.api";

export default function RewardHistoryPage() {
  const { t } = useTranslation();
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<RewardHistoryItem | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [isResendSuccess, setIsResendSuccess] = useState(false);
  const [historyStatistic, setHistoryStatistic] = useState<RewardHistoryStatistic>({
    total_redeemed_rewards: 0,
    total_sent_rewards: 0,
  });

  // Use the custom hook for API data management
  const {
    rewardHistory: currentData,
    loading,
    error,
    currentPage,
    totalPages,
    totalCount,
    setPage,
    refreshRewardHistory,
    refreshCurrentPage,
    resendReward,
    getStatistic,
    filter,
    setFilter,
  } = useRewardHistory();

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Helper function to check if any filter is active
  const hasAnyActiveFilter = () => {
    return Object.values(filter).some((value) => {
      if (typeof value === "string") {
        return value && value.trim() !== "";
      }
      if (typeof value === "object" && value !== null) {
        return Object.keys(value).length > 0;
      }
      return false;
    });
  };

  const handleRefreshAll = async () => {
    // Clear all filters first
    setFilter({
      search: "",
      userSearch: "",
      rewardSearch: "",
      status: "",
    });

    // Then refresh data
    await refreshRewardHistory();
  };

  const handleResend = (item: RewardHistoryItem) => {
    setSelectedItem(item);
    setIsResendModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsResendModalOpen(false);
    setSelectedItem(null);
    setIsResending(false);
    setIsResendSuccess(false);
  };

  const handleConfirmResend = async (item: RewardHistoryItem) => {
    setIsResending(true);
    try {
      // Call the real resend API
      const response = await resendReward(item.id);

      // Refresh the current page data to get updated information
      await refreshCurrentPage();

      // Set success state
      setIsResendSuccess(true);
      setIsResending(false);
    } catch (error) {
      console.error("Failed to resend reward:", error);
      setIsResending(false);
      // You could add error state handling here if needed
    }
  };

  useEffect(() => {
    // Fetch statistics on component mount
    const fetchStatistics = async () => {
      const stats = await getStatistic();
      setHistoryStatistic(stats);
    };

    fetchStatistics();
  }, []);

  // Stats for PageHeader component
  const stats = [
    {
      label: "Rewards Redeemed By Users",
      value: historyStatistic.total_redeemed_rewards,
      icon: (
        <div className="w-6 h-6 p-1 rounded-md flex items-center justify-center bg-[#F2FCFA] dark:bg-emerald-900/30">
          <svg
            width="17"
            height="18"
            viewBox="0 0 17 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15.5128 7.67093C15.822 9.18847 15.6016 10.7662 14.8884 12.1409C14.1752 13.5156 13.0122 14.6043 11.5935 15.2254C10.1747 15.8464 8.58595 15.9623 7.09208 15.5538C5.59821 15.1452 4.28956 14.2369 3.38436 12.9802C2.47915 11.7236 2.03212 10.1946 2.1178 8.64822C2.20348 7.10186 2.81669 5.63161 3.85518 4.48266C4.89367 3.3337 6.29467 2.57549 7.82453 2.33446C9.35439 2.09344 10.9206 2.38417 12.2621 3.15817"
              stroke="#00C49A"
              strokeWidth="1.35"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6.8457 8.34798L8.87695 10.3792L15.6478 3.6084"
              stroke="#00C49A"
              strokeWidth="1.35"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      ),
    },
    {
      label: "Rewards Used By Users",
      value: historyStatistic.total_sent_rewards,
      icon: (
        <div className="w-6 h-6 p-1 rounded-md flex items-center justify-center bg-[#F2FCFA] dark:bg-emerald-900/30">
          <svg
            width="17"
            height="18"
            viewBox="0 0 17 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.5933 15.5834C10.619 15.6475 10.6637 15.7023 10.7214 15.7402C10.7791 15.7782 10.8471 15.7976 10.9161 15.7958C10.9852 15.794 11.052 15.7712 11.1077 15.7303C11.1634 15.6895 11.2053 15.6325 11.2277 15.5672L15.6287 2.70261C15.6504 2.64261 15.6545 2.57769 15.6406 2.51543C15.6268 2.45317 15.5954 2.39615 15.5503 2.35105C15.5052 2.30594 15.4482 2.27462 15.386 2.26074C15.3237 2.24685 15.2588 2.25099 15.1988 2.27266L2.33419 6.6737C2.26885 6.69611 2.21193 6.73797 2.17106 6.79366C2.1302 6.84935 2.10735 6.91621 2.10558 6.98527C2.10381 7.05432 2.12321 7.12226 2.16117 7.17997C2.19913 7.23768 2.25383 7.2824 2.31794 7.30813L7.68721 9.46125C7.85695 9.52921 8.01116 9.63083 8.14056 9.76C8.26996 9.88917 8.37187 10.0432 8.44013 10.2128L10.5933 15.5834Z"
              stroke="#00C49A"
              strokeWidth="1.35"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M15.5499 2.35376L8.14258 9.76037"
              stroke="#00C49A"
              strokeWidth="1.35"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      ),
    },
  ];

  // Show error state if API fails
  if (error) {
    return (
      <ScrollableContentLayout
        title={t("reward_history.title")}
        subtitle={t("reward_history.subtitle")}
        stats={stats}
      >
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-red-500 text-lg font-semibold mb-4">{error}</div>
          <button
            onClick={refreshRewardHistory}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            {t("common.try_again")}
          </button>
        </div>
      </ScrollableContentLayout>
    );
  }

  return (
    <>
      <ScrollableContentLayout
        title={t("reward_history.title")}
        subtitle={t("reward_history.subtitle")}
        stats={stats}
        headerContent={
          /* Header with Refresh Button */
          <div className="flex justify-between items-center mb-4">
            <div className="text-xl font-semibold text-gray-900 dark:text-gray-200">
              {t("reward_history.title")}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefreshAll}
              disabled={loading || !hasAnyActiveFilter()}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              {t("ui.refresh")}
            </Button>
          </div>
        }
      >
        {/* Table */}
        <div className="flex-1 overflow-visible">
          <RewardHistoryTable
            data={currentData}
            loading={loading}
            onResend={handleResend}
            filter={filter}
            setFilter={setFilter}
          />
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="mt-4">
            <CommonPagination
              page={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </ScrollableContentLayout>

      {/* Resend Reward Modal */}
      <ResendRewardModal
        isOpen={isResendModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmResend}
        item={selectedItem}
        isLoading={isResending}
        isSuccess={isResendSuccess}
      />
    </>
  );
}
